import React, { useState, useEffect, useMemo } from 'react';
import {
  Table,
  Col,
  Row,
  Container,
  Badge,
  Button,
  UncontrolledTooltip,
} from 'reactstrap';
import moment from 'moment';
import { toast } from 'react-toastify';
import { Link } from 'react-router-dom';
import { formatCurrencyNumber } from '../../../util/functions';
import Api from '../../../util/api';
import Loading from '../../../shared/components/custom/Loading';
import Pagination from '../../../shared/components/pagination/Pagination';
import DeleteModal from '../../../shared/components/custom/DeleteModal';
import useModal from '../../../shared/hooks/useModal';
import t, { partial } from '../../../util/translation/translation';
import { formatDate, formatDateTime } from '../../../util/date-and-time';
import useConfig from '../../../util/useConfig';
import ShareModal from '../../../shared/components/custom/ShareModal';
import AuctionDeliveryModal from './AuctionDeliveryModal';
import ValueDisplayCounter from './ValueDisplayCounter';
import AccountGroup from 'mdi-react/ClockAlertIcon';
import useBidderBlocking from './useBidderBlocking';
import BlockBidderModal from './BlockBidderModal';

const AuctionHouseBids = ({ auctionHouseId }) => {
  const config = useConfig();
  const [loadingBids, setLoadingBids] = useState(false);
  const [selectedAuctionItem, setSelectedAuctionItem] = useState(null);
  const [bids, setBids] = useState([]);
  const [auctionItems, setAuctionItems] = useState([]);
  const [selectedBid, setSelectedBid] = useState(null);
  const [handleModal, visible, deleteBidId] = useModal();
  const { isBlocked, blockUser } = useBidderBlocking();
  // The bidder waiting for the admin to confirm the block
  const [userToBlock, setUserToBlock] = useState(null);
  const [pagination, setPagination] = useState({
    total: 0,
    currentPage: 1,
    perPage: 10,
  });

  const p = partial('auctionObjectsShared');
  const s = partial('shared');

  const notPaidCount = useMemo(
    () =>
      auctionItems.reduce((tot, curr) => {
        return tot + (curr?.bids[0]?.transactionId ? 0 : 1) || 0;
      }, 0),
    [auctionItems]
  );

  const getAuctionHouseItems = async (displayLoading = true) => {
    if (displayLoading) {
      setLoadingBids(true);
    }
    try {
      const resAuctionItems =
        await Api.auctions.getAuctionItemsForHouse(auctionHouseId);
      setAuctionItems(resAuctionItems);
    } catch (err) {
      toast.error(JSON.stringify(err));
    } finally {
      if (displayLoading) {
        setLoadingBids(false);
      }
    }
  };

  const contactUnpaidBids = async () => {
    setLoadingBids(true);

    try {
      const contactRes = await Api.auctions.contactUnpaidBids(auctionHouseId);
      toast.success(p('unpaidBidsContacted', { amount: contactRes.count }));
    } catch (err) {
      toast.error(JSON.stringify(err));
    } finally {
      setLoadingBids(false);
    }
  };
  const updateDeliveryStatus = async (values) => {
    setLoadingBids(true);

    try {
      const payload = {
        deliveryStatus: values.deliveryStatus.value,
      };
      await Api.auctions.updateDeliveryStatus(selectedBid.id, payload);
      toast.success(p('deliveryStatusUpdated'));
      setSelectedBid(null);
      getBids();
    } catch (err) {
      toast.error(JSON.stringify(err));
      setLoadingBids(false);
    }
  };
  const buttonUpdateDeliveryStatus = async (bid) => {
    setLoadingBids(true);
    try {
      const payload = {
        deliveryStatus:
          bid.deliveryDetails.deliveryStatus === 'SENT' ? 'NOT_SENT' : 'SENT',
      };
      await Api.auctions.updateDeliveryStatus(bid.id, payload);
      toast.success(p('deliveryStatusUpdated'));
      getBids();
    } catch (err) {
      toast.error(JSON.stringify(err));
      setLoadingBids(false);
    }
  };

  useEffect(() => {
    if (auctionHouseId) {
      getAuctionHouseItems();
    }
  }, [auctionHouseId]);

  const remainingTime = (date) => {
    const minutes = date.diff(moment(), 'minutes');
    if (minutes < 0) {
      return formatDate(date);
    }
    if (minutes < 60) {
      return `${minutes}m`;
    }
    const hours = date.diff(moment(), 'hours');
    if (hours < 24) {
      return `${hours}t ${Math.floor(minutes - hours * 60)}m`;
    }
    const days = date.diff(moment(), 'days');
    return `${days}d ${Math.floor(hours - days * 24)}t ${Math.floor(minutes - hours * 60)}m`;
  };

  const getBids = async (currentPage = 1, pageSize = 10) => {
    setLoadingBids(true);
    try {
      const resBids = await Api.auctions.getAuctionItemBids(
        selectedAuctionItem.id,
        { page: currentPage, take: pageSize }
      );
      const userIds = [...new Set(resBids.data.map((item) => item.userId))];
      const { data: usersByIds } = await Api.users.getUsersByCloudIds(userIds);
      const userByIdMap = {};
      for (let i = 0; i < usersByIds.length; i++) {
        userByIdMap[usersByIds[i].userCloudId] = usersByIds[i];
      }
      setPagination((old) => ({ ...old, total: resBids.count }));

      const sortedBids = resBids.data
        .sort((a, b) => b.amount - a.amount)
        .map((item) => ({ ...item, user: userByIdMap[item.userId] }));
      setBids(sortedBids);
      setLoadingBids(false);
    } catch (err) {
      toast.error(err);
      setLoadingBids(false);
    }
  };

  const removeBid = async () => {
    setLoadingBids(true);
    try {
      await Api.auctions.deleteAuctionItemBid(deleteBidId);
    } catch (err) {
      toast.error(err);
    } finally {
      handleModal();
      getAuctionHouseItems(false);
      getBids();
    }
  };

  const renderAuctionItems = () => {
    return auctionItems.map((auctionItem, i) => (
      <tr
        style={{ background: i % 2 ? '#fff' : '#F9FAFB' }}
        key={`auctionItem-${auctionItem.id}`}
      >
        <td>
          {auctionItem.name}
          <br />
          {auctionItem?.paymentAvailable &&
            auctionItem?.bids[0]?.transactionId && (
              <>
                <Badge color={'success'}>{t('transactions.status-paid')}</Badge>
                <Badge
                  className="my-1 ml-1"
                  color={
                    auctionItem?.bids[0]?.deliveryDetails?.deliveryType ===
                    'PICK_UP'
                      ? 'primary'
                      : auctionItem?.bids[0]?.deliveryDetails
                            ?.deliveryStatus === 'SENT'
                        ? 'success'
                        : 'warning'
                  }
                >
                  {`${
                    auctionItem?.bids[0]?.deliveryDetails?.deliveryType ===
                    'PICK_UP'
                      ? p('pickup')
                      : p('delivery')
                  } ${auctionItem?.bids[0]?.deliveryDetails?.deliveryStatus === 'SENT' ? s('sent') : ''}`}
                </Badge>
              </>
            )}
        </td>
        <td>
          <b>
            {formatCurrencyNumber(auctionItem.lastBid)} {config.currencyCaps}
          </b>
          <br />
          <Badge
            color={
              moment(auctionItem.endDate).isAfter(Date.now())
                ? 'success'
                : 'secondary'
            }
          >
            {remainingTime(moment(auctionItem.endDate))}
          </Badge>
        </td>
        <td></td>
        <td></td>
        <td>
          <Button
            onClick={() => setSelectedAuctionItem(auctionItem)}
            color="primary"
            outline
            size="sm"
          >
            {p('seeBids')}
          </Button>
        </td>
      </tr>
    )); // TODO we should have proper currency handling here i18n
  };

  const onChangePage = (pager) => {
    if (
      pager.pageSize !== pagination.perPage ||
      pager.currentPage !== pagination.currentPage
    ) {
      setPagination((old) => ({
        ...old,
        currentPage: pager.currentPage,
        perPage: pager.pageSize,
      }));
      getBids(pager.currentPage, pager.pageSize);
    }
  };

  useEffect(() => {
    if (selectedAuctionItem) {
      getBids(1, bids?.per_page ?? 10);
    }
  }, [selectedAuctionItem]);

  const renderBids = () => {
    return bids.map((bid, i) => (
      <tr
        style={{ background: i % 2 ? '#fff' : '#F9FAFB' }}
        key={`bids-${bid.id}`}
      >
        <td>{formatDateTime(bid.createdAt)}</td>
        <td>
          <div className="flex-Vcenter">
            <Link
              to={`/users/edit/${bid.user?.userCloudId}`}
            >{`${bid.user?.firstname} ${bid.user?.lastname} (${bid.user?.phoneNumber})`}</Link>
          </div>
        </td>
        <td>
          <b>
            {formatCurrencyNumber(bid.amount)} {config.currencyCaps}
          </b>
        </td>
        <td>
          {i === 0 && (
            <Badge
              color={
                bid?.transactionId
                  ? 'success'
                  : selectedAuctionItem?.paymentAvailable
                    ? 'warning'
                    : 'danger'
              }
            >
              {bid?.transactionId
                ? t('transactions.status-completed')
                : selectedAuctionItem?.paymentAvailable
                  ? t('transactions.status-pending')
                  : t('transactions.paymentsNotAvailable')}
            </Badge>
          )}
        </td>
        <td>
          {i === 0 &&
            (bid?.deliveryDetails?.deliveryType === 'PICK_UP' ? (
              p('pickup')
            ) : (
              <button
                onClick={() => setSelectedBid(bid)}
                className="outline-badge success"
              >
                {s('details')}
              </button>
            ))}
        </td>
        <td>
          {i === 0 && (
            <Badge
              color={
                bid?.deliveryDetails?.deliveryStatus === 'SENT'
                  ? 'success'
                  : 'warning'
              }
            >
              {bid?.deliveryDetails?.deliveryStatus === 'SENT'
                ? p('deliveryStatusSent')
                : p('deliveryStatusNotSent')}
            </Badge>
          )}
        </td>
        <td>
          {i === 0 && (
            <button
              onClick={() => buttonUpdateDeliveryStatus(bid)}
              className={`outline-badge ${bid?.deliveryDetails?.deliveryStatus === 'SENT' ? 'danger' : 'success'}`}
            >
              {`${p('changeTo')}: ${bid?.deliveryDetails?.deliveryStatus === 'SENT' ? p('deliveryStatusNotSent') : p('deliveryStatusSent')}`}
            </button>
          )}
        </td>
        <td>
          {isBlocked(bid.userId) ? (
            <Badge color="secondary">{p('bidderIsBlocked')}</Badge>
          ) : (
            <button
              onClick={() =>
                setUserToBlock({
                  userId: bid.userId,
                  userName: bid.user
                    ? `${bid.user.firstname} ${bid.user.lastname}`
                    : '',
                })
              }
              className="outline-badge danger"
            >
              {p('blockBidder')}
            </button>
          )}
        </td>
        <td>
          <button
            onClick={() => handleModal(bid.id)}
            className="outline-badge danger"
          >
            {s('deleteCaps')}
          </button>
        </td>
      </tr>
    ));
  };

  return (
    <Container>
      <div className="bold-text flex alignItems-center flex-HSpaceBetween">
        {notPaidCount !== 0 && (
          <>
            <UncontrolledTooltip placement="bottom" target="notPaidCount1">
              {p('notPaidCount')}
            </UncontrolledTooltip>
            <ValueDisplayCounter
              id="notPaidCount1"
              color={'warning'}
              icon={
                <AccountGroup style={{ width: 24, display: 'inline-block' }} />
              }
              text={`${p('notPaidCount')}: ${notPaidCount}`}
            />
          </>
        )}
        <Button onClick={contactUnpaidBids} color="primary" size="sm">
          {p('contactUnpaidBids')}
        </Button>
      </div>
      <Row>
        <DeleteModal
          handleModal={handleModal}
          visible={visible}
          type={p('theBidSmall')}
          modalAction={removeBid}
        />
        <BlockBidderModal
          user={userToBlock}
          onCancel={() => setUserToBlock(null)}
          onBlock={(block) => {
            blockUser(block);
            setUserToBlock(null);
          }}
        />
        <ShareModal
          visible={selectedBid}
          title={p('deliveryDetails')}
          handleModal={() => {
            setSelectedBid(null);
          }}
        >
          <AuctionDeliveryModal
            selectedBid={selectedBid}
            onSubmit={updateDeliveryStatus}
          />
        </ShareModal>
        <Loading loading={loadingBids} />
        <Col xl={5} lg={6} md={12} className="marginBottom-20">
          <p className="bold">{p('auctionObjects')}</p>
          <div
            style={{
              display: 'flex',
              paddingBottom: 5,
              marginTop: 5,
              background: '#fff',
              borderRadius: 6,
              border: '1px solid rgba(0,0,0,0.08)',
            }}
          >
            <Table responsive striped>
              <thead>
                <tr>
                  <th className="bold" style={{ color: '#4D576A' }}>
                    {p('object')}
                  </th>
                  <th className="bold" style={{ color: '#4D576A' }}>
                    {p('highestBid')}
                  </th>
                  <th />
                </tr>
              </thead>
              <tbody>{renderAuctionItems()}</tbody>
            </Table>
          </div>
        </Col>
        <Col xl={7} lg={6} md={12}>
          <p className="bold">
            {selectedAuctionItem
              ? `${selectedAuctionItem.name}`
              : p('chooseAuctionObject')}
          </p>
          <div
            style={{
              display: 'flex',
              paddingBottom: 5,
              marginTop: 5,
              background: '#fff',
              borderRadius: 6,
              border: '1px solid rgba(0,0,0,0.08)',
            }}
          >
            <Table responsive striped>
              <thead>
                <tr>
                  <th className="bold" style={{ color: '#4D576A' }}>
                    {s('date')}
                  </th>
                  <th className="bold" style={{ color: '#4D576A' }}>
                    {s('name')}
                  </th>
                  <th className="bold" style={{ color: '#4D576A' }}>
                    {s('amount')}
                  </th>
                  <th className="bold" style={{ color: '#4D576A' }}>
                    {p('payment')}
                  </th>
                  <th className="bold" style={{ color: '#4D576A' }}>
                    {p('delivery')}
                  </th>
                  <th className="bold" style={{ color: '#4D576A' }}>
                    {p('deliveryStatus')}
                  </th>
                  <th />
                  <th />
                  <th />
                </tr>
              </thead>
              <tbody>{renderBids()}</tbody>
            </Table>
          </div>
          <Pagination
            items={pagination.total || 0}
            pageSize={bids.per_page}
            onChangePage={onChangePage}
          />
        </Col>
      </Row>
    </Container>
  );
};

export default AuctionHouseBids;
